import React, { useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Text from "@/components/ui/text";
import SearchInput from "@/components/ui/search-input";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import UserAvatar from "@/components/controls/UserAvatar";
import { useUsers } from "@/entities/users/hooks";
import { IconButton } from "@/components/ui/icon-button";
import { X } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import DropdownMenuListItem from "@/components/controls/OptionsDropdownMenu/DropdownMenuListItem";
import { DatePreset } from "@/components/controls/datePresets";
import { formatPercentage } from "@/utils/numbers";
import { DateRange } from "@/components/controls/DateRangePicker";
import { displaySelectedDates } from "@/components/controls/displaySelectedDates";
import { getCurrentLocale } from "@/utils/locale";
import { normalizeDateRange } from "@/utils/dateRange";
import FilterNumericSubmenu from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterNumericSubmenu";
import FilterDateSubmenu from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterDateSubmenu";
import FilterBooleanSubmenu from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterBooleanSubmenu";
import FilterTextSubmenu from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterTextSubmenu";
import { NumericCondition } from "@/pages/deals/numericCondition";
import {
  useFilterOptions,
  useFilterToggleWithNotIn,
  isNotInFilter,
  extractFilterValues,
  wrapWithNotIn,
  getNumericCondition,
  getNumericAmount
} from "@/hooks/useFilterOptions";
import {
  NumericFilterValue,
  BooleanFilterValue,
  TextFilterValue,
  TextCondition,
  TEXT_CONDITIONS,
  BOOLEAN_FILTER_TRANSLATION_KEY,
  TEXT_FILTER_TRANSLATION_KEY,
  parseTextFilterValue,
  buildTextFilterValue
} from "@/components/controls/ToolbarActions/ToolbarFilterButton/types";

export type FilterCondition = "is" | "isNot";

export type FilterChipVariant =
  | "default"
  | "nonRemovable"
  | "noCondition"
  | "dropOnly";

interface Props {
  filterKey: string;
  filterLabel: string;
  filterOption: DataTableFilter;
  filters: Filters;
  setFilters: FiltersSetter;
  onConditionChange?: (condition: FilterCondition) => void;
  onRemove?: () => void;
  selectedValuesDisplay?: string;
  className?: string;
  size?: "sm" | "lg";
  variant?: FilterChipVariant;
  isMonetary?: boolean;
  isPercentage?: boolean;
}

const FilterChip: React.FC<Props> = ({
  filterKey,
  filterLabel,
  filterOption,
  filters,
  setFilters,
  onConditionChange,
  onRemove,
  selectedValuesDisplay,
  className,
  size = "sm",
  variant = "default",
  isMonetary = false,
  isPercentage = false
}) => {
  const { t } = useTranslation();
  const [isConditionOpen, setIsConditionOpen] = useState(false);
  const [isValuesOpen, setIsValuesOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const isSelectingRef = useRef(false);

  const filterType = filterOption.filterType;
  const currentValue = filters[filterKey as keyof Filters];

  const {
    combinedOptions,
    selectedValues,
    useServerSearch,
    isLoading,
    onSearch,
    showAvatar,
    shouldShowSearch
  } = useFilterOptions({
    filterOption,
    filters,
    searchValue,
    alwaysEnabled: true
  });

  const toggleValue = useFilterToggleWithNotIn(filterKey, setFilters);

  const userIds = useMemo(
    () => (showAvatar ? combinedOptions.map((o) => String(o.value)) : []),
    [showAvatar, combinedOptions]
  );
  const { users } = useUsers(userIds, { enabled: showAvatar });
  const usersMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  const currentCondition = useMemo((): FilterCondition => {
    if (isNotInFilter(currentValue)) {
      return "isNot";
    }
    return "is";
  }, [currentValue]);

  const allSelectedLabels = useMemo(() => {
    return selectedValues
      .map((val) => {
        const option = combinedOptions.find((opt) => opt.value === val);
        return option?.label || String(val);
      })
      .join(", ");
  }, [selectedValues, combinedOptions]);

  const firstSelectedOption = useMemo(() => {
    if (selectedValues.length === 0) return null;
    return (
      combinedOptions.find((opt) => opt.value === selectedValues[0]) || null
    );
  }, [selectedValues, combinedOptions]);

  const locale = getCurrentLocale();

  const displayText = useMemo(() => {
    if (selectedValuesDisplay) return selectedValuesDisplay;

    if (filterType.type === "boolean" && currentValue) {
      const boolValue = (currentValue as BooleanFilterValue).eq;
      const trueText = filterType.trueLabel
        ? t(filterType.trueLabel)
        : t(`${BOOLEAN_FILTER_TRANSLATION_KEY}.true`);
      const falseText = filterType.falseLabel
        ? t(filterType.falseLabel)
        : t(`${BOOLEAN_FILTER_TRANSLATION_KEY}.false`);
      return boolValue === true
        ? trueText
        : boolValue === false
          ? falseText
          : `${t("select")}...`;
    }

    if (filterType.type === "date" && currentValue) {
      const dateFilterValue = isNotInFilter(currentValue)
        ? (currentValue as { not_in: { dateRange: string | DateRange } }).not_in
        : (currentValue as { dateRange: string | DateRange });

      if (dateFilterValue?.dateRange) {
        if (typeof dateFilterValue.dateRange === "string") {
          return (
            Object.values(DatePreset).includes(dateFilterValue.dateRange as DatePreset)
              ? t(`datatoolbar.filters.presets.${dateFilterValue.dateRange}`)
              : dateFilterValue.dateRange
          );
        }
        const normalizedRange = normalizeDateRange(dateFilterValue.dateRange);
        if (normalizedRange) {
          return displaySelectedDates(normalizedRange, (date: Date) =>
            date?.toLocaleDateString(locale)
          );
        }
      }
      return `${t("select")}...`;
    }

    if (filterType.type === "numeric" && currentValue) {
      const { gt, lt, eq, min, max } = currentValue as {
        gt?: number;
        lt?: number;
        eq?: number;
        min?: number;
        max?: number;
      };
      const formatNumber = (n: number) => {
        if (isPercentage) return formatPercentage(n * 100);
        if (isMonetary)
          return `${filterType.currencySymbol ?? "$"} ${n.toLocaleString()}`;
        return n.toLocaleString();
      };
      const singleValue = gt ?? lt ?? eq;
      if (singleValue !== undefined) return formatNumber(singleValue);
      if (min !== undefined || max !== undefined) {
        return `${min !== undefined ? formatNumber(min) : ""} - ${max !== undefined ? formatNumber(max) : ""}`;
      }
      return `${t("select")}...`;
    }

    if (filterType.type === "text" && currentValue) {
      const textValue = currentValue as TextFilterValue;
      const parsed = parseTextFilterValue(textValue);
      if (parsed.condition === "isEmpty" || parsed.condition === "isNotEmpty") {
        return "";
      }
      if (parsed.value) {
        return `"${parsed.value}"`;
      }
      return `${t("select")}...`;
    }

    if (selectedValues.length === 0) {
      return `${t("select")}...`;
    }

    if (selectedValues.length === 1) {
      return firstSelectedOption?.label || t("unknown");
    }

    const firstLabel = firstSelectedOption?.label || t("unknown");
    const remainingCount = selectedValues.length - 1;

    return `${firstLabel} +${remainingCount}`;
  }, [
    selectedValues,
    selectedValuesDisplay,
    firstSelectedOption,
    t,
    filterType,
    currentValue,
    locale,
    isMonetary,
    isPercentage
  ]);

  const showTooltip = selectedValues.length > 1;

  const showLabel = variant !== "dropOnly";
  const showCondition = variant === "default" || variant === "nonRemovable";
  const showRemoveButton = variant === "default" || variant === "noCondition";

  const isNumericFilter = filterType.type === "numeric";
  const isDateFilter = filterType.type === "date";
  const isBooleanFilter = filterType.type === "boolean";
  const isTextFilter = filterType.type === "text";

  const numericValue = isNumericFilter
    ? (currentValue as NumericFilterValue | undefined)
    : undefined;

  const currentNumericCondition = getNumericCondition(numericValue);

  // Get current numeric amount from filter value (for gt, lt, et conditions)
  // For BETWEEN, use min value as fallback when switching to another condition
  const currentNumericAmount = useMemo((): number | null => {
    const amount = getNumericAmount(numericValue);
    if (amount !== null) return amount;
    if (numericValue?.min !== undefined) return numericValue.min;
    if (numericValue?.max !== undefined) return numericValue.max;
    return null;
  }, [numericValue]);

  // Get current text condition from filter value
  const currentTextCondition = useMemo((): TextCondition | null => {
    if (!isTextFilter || !currentValue) return null;
    const parsed = parseTextFilterValue(currentValue as TextFilterValue);
    return parsed.condition;
  }, [isTextFilter, currentValue]);

  const isTextConditionWithoutValue =
    isTextFilter &&
    (currentTextCondition === "isEmpty" ||
      currentTextCondition === "isNotEmpty");

  // Get current text value from filter
  const currentTextValue = useMemo((): string | undefined => {
    if (!isTextFilter || !currentValue) return undefined;
    const parsed = parseTextFilterValue(currentValue as TextFilterValue);
    return parsed.value;
  }, [isTextFilter, currentValue]);

  const handleNumericConditionSelect = (newCondition: NumericCondition) => {
    // If we have an existing amount, update the filter with the new condition
    if (currentNumericAmount !== null) {
      setFilters((prev) => ({
        ...prev,
        [filterKey]: { [newCondition]: currentNumericAmount }
      }));
    }
    setIsConditionOpen(false);
  };

  const handleTextConditionSelect = (newCondition: TextCondition) => {
    if (newCondition === "isEmpty" || newCondition === "isNotEmpty") {
      setFilters((prev) => ({
        ...prev,
        [filterKey]: buildTextFilterValue(newCondition)
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        [filterKey]: buildTextFilterValue(newCondition, currentTextValue)
      }));
    }
    setIsConditionOpen(false);
  };

  const handleConditionSelect = (newCondition: FilterCondition) => {
    setFilters((prev) => {
      const current = prev[filterKey as keyof Filters];
      if (isDateFilter) {
        if (!current) {
          setIsConditionOpen(false);
          return prev;
        }
        const dateValue = isNotInFilter(current)
          ? (current as { not_in: { dateRange: string | DateRange } }).not_in
          : current;

        if (newCondition === "isNot") {
          return {
            ...prev,
            [filterKey]: { not_in: dateValue }
          };
        } else {
          return {
            ...prev,
            [filterKey]: dateValue
          };
        }
      }

      const currentValues = extractFilterValues(current);
      if (currentValues.length === 0) {
        setIsConditionOpen(false);
        return prev;
      }
      if (newCondition === "isNot") {
        return {
          ...prev,
          [filterKey]: wrapWithNotIn(currentValues)
        };
      } else {
        return {
          ...prev,
          [filterKey]: currentValues
        };
      }
    });

    onConditionChange?.(newCondition);
    setIsConditionOpen(false);
  };

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (useServerSearch && onSearch) {
        onSearch(value);
      }
    },
    [useServerSearch, onSearch]
  );

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-lg border-[0.5px] border-border-badge-default bg-badges-default",
        className
      )}
      data-test={`filter-chip-${filterKey}`}
    >
      {showLabel && (
        <div
          className={cn(
            "py-1 border-r-[0.5px] border-border-badge-default",
            size === "lg" ? "px-2" : "px-[6px]"
          )}
        >
          <Text variant={showCondition ? "body" : "bodySecondary"}>
            {filterLabel}
          </Text>
        </div>
      )}
      {showCondition &&
        !isNumericFilter &&
        !isBooleanFilter &&
        !isTextFilter && (
          <DropdownMenu
            open={isConditionOpen}
            onOpenChange={setIsConditionOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                className={cn(
                  "py-1 border-l-0 border-t-0 border-b-0 border-r-[0.5px] border-border-badge-default rounded-none",
                  isConditionOpen && "bg-surface-focus-selected",
                  size === "lg" ? "px-2" : "px-[6px]"
                )}
                variant="secondaryNoBg"
                size="icon"
              >
                <Text variant="bodySecondary">
                  {currentCondition === "is"
                    ? t("datatoolbar.filters.conditions.is")
                    : t("datatoolbar.filters.conditions.isNot")}
                </Text>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={cn(
                "bg-surface-elevation2 z-50 border border-badges-default rounded-[10px] p-1 w-32",
                "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut"
              )}
              align="start"
              sideOffset={4}
            >
              <DropdownMenuListItem
                option="is"
                text={t("datatoolbar.filters.conditions.is")}
                onSelect={() => handleConditionSelect("is")}
                selected={currentCondition === "is"}
              />
              <DropdownMenuListItem
                option="isNot"
                text={t("datatoolbar.filters.conditions.isNot")}
                onSelect={() => handleConditionSelect("isNot")}
                selected={currentCondition === "isNot"}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      {showCondition && isBooleanFilter && (
        <div
          className={cn(
            "py-1 border-l-0 border-t-0 border-b-0 border-r-[0.5px] border-border-badge-default",
            size === "lg" ? "px-2" : "px-[6px]"
          )}
        >
          <Text variant="bodySecondary">
            {t("datatoolbar.filters.conditions.is")}
          </Text>
        </div>
      )}
      {showCondition && isNumericFilter && (
        <DropdownMenu open={isConditionOpen} onOpenChange={setIsConditionOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className={cn(
                "py-1 border-l-0 border-t-0 border-b-0 border-r-[0.5px] border-border-badge-default rounded-none",
                isConditionOpen && "bg-surface-focus-selected",
                size === "lg" ? "px-2" : "px-[6px]"
              )}
              variant="secondaryNoBg"
              size="icon"
            >
              <Text variant="bodySecondary">
                {currentNumericCondition === NumericCondition.GT
                  ? t("datatoolbar.filters.MRRConditions.gt")
                  : currentNumericCondition === NumericCondition.LT
                    ? t("datatoolbar.filters.MRRConditions.lt")
                    : currentNumericCondition === NumericCondition.EQ
                      ? t("datatoolbar.filters.MRRConditions.eq")
                      : currentNumericCondition === NumericCondition.BETWEEN
                        ? t("datatoolbar.filters.MRRConditions.between")
                        : t("datatoolbar.filters.conditions.is")}
              </Text>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={cn(
              "bg-surface-elevation2 z-50 border border-badges-default rounded-md p-1 w-40",
              "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut"
            )}
            align="start"
            sideOffset={4}
          >
            <DropdownMenuListItem
              option={NumericCondition.GT}
              text={t("datatoolbar.filters.MRRConditions.gt")}
              onSelect={() => handleNumericConditionSelect(NumericCondition.GT)}
              selected={currentNumericCondition === NumericCondition.GT}
            />
            <DropdownMenuListItem
              option={NumericCondition.LT}
              text={t("datatoolbar.filters.MRRConditions.lt")}
              onSelect={() => handleNumericConditionSelect(NumericCondition.LT)}
              selected={currentNumericCondition === NumericCondition.LT}
            />
            <DropdownMenuListItem
              option={NumericCondition.EQ}
              text={t("datatoolbar.filters.MRRConditions.eq")}
              onSelect={() => handleNumericConditionSelect(NumericCondition.EQ)}
              selected={currentNumericCondition === NumericCondition.EQ}
            />
            {currentNumericCondition === NumericCondition.BETWEEN && (
              <DropdownMenuListItem
                option={NumericCondition.BETWEEN}
                text={t("datatoolbar.filters.MRRConditions.between")}
                onSelect={() => setIsConditionOpen(false)}
                selected={true}
              />
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {showCondition && isTextFilter && (
        <DropdownMenu open={isConditionOpen} onOpenChange={setIsConditionOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className={cn(
                "py-1 border-l-0 border-t-0 border-b-0 border-r-[0.5px] border-border-badge-default rounded-none",
                isConditionOpen && "bg-surface-focus-selected",
                size === "lg" ? "px-2" : "px-[6px]"
              )}
              variant="secondaryNoBg"
              size="icon"
            >
              <Text variant="bodySecondary">
                {currentTextCondition
                  ? t(`${TEXT_FILTER_TRANSLATION_KEY}.${currentTextCondition}`)
                  : t("datatoolbar.filters.conditions.is")}
              </Text>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={cn(
              "bg-surface-elevation2 z-50 border border-badges-default rounded-md p-1 w-44",
              "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut"
            )}
            align="start"
            sideOffset={4}
          >
            {TEXT_CONDITIONS.map((condition) => (
              <DropdownMenuListItem
                key={condition}
                option={condition}
                text={t(`${TEXT_FILTER_TRANSLATION_KEY}.${condition}`)}
                onSelect={() => handleTextConditionSelect(condition)}
                selected={currentTextCondition === condition}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {!isTextConditionWithoutValue && (
        <Popover
          open={isValuesOpen}
          onOpenChange={(open) => {
            if (!open && isSelectingRef.current) {
              return;
            }
            setIsValuesOpen(open);
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  className={cn(
                    "py-1 border-border-badge-default",
                    isValuesOpen && "bg-surface-focus-selected",
                    showRemoveButton
                      ? "border-r-[0.5px] border-l-0 border-t-0 border-b-0 rounded-none"
                      : variant === "dropOnly"
                        ? "rounded-lg border-none"
                        : "border-none rounded-none",
                    size === "lg" ? "px-2" : "px-[6px]"
                  )}
                  variant="secondaryNoBg"
                  size="icon"
                >
                  {firstSelectedOption?.icon && firstSelectedOption.icon}
                  <Text variant="body" className="text-primary truncate">
                    {displayText}
                  </Text>
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            {showTooltip && !isValuesOpen && (
              <TooltipContent>{allSelectedLabels}</TooltipContent>
            )}
          </Tooltip>
          <PopoverContent
            padding={4}
            className={cn(
              "gap-1",
              filterType.type === "numeric" ||
                filterType.type === "date" ||
                filterType.type === "boolean" ||
                filterType.type === "text"
                ? "w-auto"
                : "w-64 max-h-[300px]"
            )}
            align="start"
            sideOffset={4}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {filterType.type === "numeric" ? (
              <FilterNumericSubmenu
                key={`numeric-submenu-${isValuesOpen}`}
                filterOption={filterOption}
                filterLabel={filterLabel}
                filters={filters}
                setFilters={setFilters}
                onClose={() => setIsValuesOpen(false)}
                excludeBetween
                skipConditionSelection
              />
            ) : filterType.type === "date" ? (
              <FilterDateSubmenu
                filterOption={filterOption}
                filters={filters}
                setFilters={setFilters}
              />
            ) : filterType.type === "boolean" ? (
              <FilterBooleanSubmenu
                filterOption={filterOption}
                filters={filters}
                setFilters={setFilters}
                onClose={() => setIsValuesOpen(false)}
              />
            ) : isTextFilter ? (
              <FilterTextSubmenu
                filterOption={filterOption}
                filterLabel={filterLabel}
                filters={filters}
                setFilters={setFilters}
                onClose={() => setIsValuesOpen(false)}
              />
            ) : (
              <div className="flex flex-col gap-2">
                {shouldShowSearch && (
                  <div className="sticky top-0 bg-surface-elevation2 z-10">
                    <SearchInput
                      placeholder={t("search")}
                      value={searchValue}
                      onChange={handleSearchChange}
                      hideIcon
                      clearButtonSize="xs"
                      clearButtonColor="secondary"
                    />
                  </div>
                )}
                <div className="flex flex-col overflow-y-auto max-h-[250px]">
                  {isLoading ? (
                    <Text
                      variant="bodyTertiary"
                      className="px-2 py-4 text-center"
                    >
                      {t("loading")}
                    </Text>
                  ) : (
                    <>
                      {combinedOptions.map((option) => {
                        const isSelected = selectedValues.includes(
                          option.value
                        );
                        return (
                          <div
                            key={String(option.value)}
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              isSelectingRef.current = true;
                            }}
                            onClick={() => {
                              isSelectingRef.current = true;
                              toggleValue(option.value);
                              setTimeout(() => {
                                isSelectingRef.current = false;
                              }, 200);
                            }}
                            className={cn(
                              "flex items-center gap-2 px-2 py-2 cursor-pointer rounded-lg",
                              "hover:bg-surface-hover-soft active:bg-surface-pressed",
                              "transition-all duration-200 ease-in-out"
                            )}
                          >
                            <Checkbox checked={isSelected} className="mr-1" />
                            {option.icon && option.icon}
                            {showAvatar &&
                              !option.icon &&
                              usersMap.has(String(option.value)) && (
                                <UserAvatar
                                  size="M"
                                  fallbackClassName="bg-gradient-to-t from-slate-600 to-tertiary"
                                  userData={usersMap.get(String(option.value))}
                                />
                              )}
                            <Text
                              variant={option.labelVariant || "body"}
                              className={cn(
                                "flex-1",
                                option.isNoValue && "italic text-tertiary"
                              )}
                            >
                              {option.label}
                            </Text>
                          </div>
                        );
                      })}
                      {combinedOptions.length === 0 && (
                        <Text
                          variant="bodyTertiary"
                          className="px-2 py-4 text-center"
                        >
                          {t("common.noResults", "No results found")}
                        </Text>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
      {showRemoveButton && (
        <IconButton
          iconColor="secondary"
          variant="iconNoBg"
          size="md"
          onClick={onRemove}
          className="border-none bg-transparent rounded-l-none"
        >
          <X />
        </IconButton>
      )}
    </div>
  );
};

export default FilterChip;
